import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import FusionChainFlow from "./FusionChainFlow";
import FusionWhyPopover from "./FusionWhyPopover";
import FusionLeverageCard from "./FusionLeverageCard";
import FusionTrackPanel from "./FusionTrackPanel";
import { HelpDot } from "./FusionBits";
import FusionConsequence from "./FusionConsequence";
import FusionReach from "./FusionReach";
import {
  EXPECTED_LABEL, NON_ADDITIVE_TEXT, BREAK_POINT, BREAK_POINT_HINT, SCENARIO_LABEL, fetchChain, fetchWhatIf, formatDate, formatMoney, laneLabel, nodeKey, severityClass,
} from "./fusionApi";

const SEVERITY_ORDER = ["Critical", "High", "Medium", "Low"];
const CLOSED = { total: false, track: false, history: false, whatif: false, resolve: false, objects: false };

const Toggle = ({ on, onClick, children, title }) => (
  <button onClick={onClick} title={title}
    className={`text-xs px-2.5 py-1 rounded-lg border ${on ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-theme-secondary text-content-primary border-border"}`}>
    {children}
  </button>
);

/**
 * One chain, in the guide's progressive-disclosure shape: header and badge, the flow with cumulative $
 * on arrows, "How this chain plays out" (open), then one row of collapsed sections: Correlated total,
 * Track chain, How it formed, What-if, ◆ Break Point and Affected objects. Every section mounts only
 * when opened, and all collapse again on re-anchor. An anchor the user cannot see answers 404, which
 * reads exactly like one that no longer exists.
 *
 * `anchor.focus` ({ distance, lane }: a list path segment the user clicked) highlights that step's risks
 * and objects in the flow. Every re-anchor from inside the view passes this chain as `from`, so the panel
 * can keep a trail; `previous` is the chain to go back to ("← Back to …").
 */
const FusionChainView = ({ anchor, onOpen, onBack, onActionDone, refreshNonce, previous, onBackTo }) => {
  const apiCall = useApi();
  const [detail, setDetail] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | failed
  const [whyLinks, setWhyLinks] = useState(null);
  const [open, setOpen] = useState(CLOSED);
  // Only the newest request may write: re-anchoring quickly must not show an older chain last.
  const latest = useRef(0);
  const seenNonce = useRef(refreshNonce);

  const load = useCallback(async ({ refresh = false } = {}) => {
    const id = ++latest.current;
    setState("loading");
    try {
      // A refreshNonce re-read (after Refresh or an action) goes with refresh=true: the server it
      // lands on may not be the one that rebuilt for the summary, and without a distributed cache
      // there is no generation bump to move it off a workspace built before the action. The server
      // reuses a build from the last few seconds, so this costs at most one rebuild per server.
      const next = await fetchChain(apiCall, { anchorType: anchor.type, anchorId: anchor.id, refresh });
      if (id !== latest.current) return;
      setDetail(next);
      setState("ready");
    } catch (err) {
      if (id !== latest.current) return;
      setDetail(null);
      setState(err?.response?.status === 404 ? "missing" : "failed");
    }
  }, [apiCall, anchor.type, anchor.id]);

  useEffect(() => {
    const refresh = refreshNonce !== seenNonce.current;
    seenNonce.current = refreshNonce;
    load({ refresh });
  }, [load, refreshNonce]);
  useEffect(() => { setOpen(CLOSED); }, [anchor.type, anchor.id]);

  const members = useMemo(() => (detail ? detail.steps.flatMap((s) => s.members) : []), [detail]);
  // The list segment the user clicked: that step's members, by key.
  const focusKeys = useMemo(() => {
    const f = anchor.focus;
    if (!detail || !f) return null;
    const step = detail.steps.find((s) => s.distance === f.distance && s.lane === f.lane);
    return step ? new Set(step.members.map((m) => nodeKey(m.type, m.id))) : null;
  }, [detail, anchor.focus]);
  // Join-point refs are labelled too, so the "why?" popover never falls back to "Type id".
  const labels = useMemo(() => Object.fromEntries([
    ...(detail?.riskPath || []).flatMap((h) => [h.hop, ...(h.joinPoints || [])]),
    ...members,
  ].map((m) => [nodeKey(m.type, m.id), m.label])), [detail, members]);
  const labelOf = useCallback((type, id) => labels[nodeKey(type, id)] || `${type} ${id}`, [labels]);
  const contextByNode = useMemo(() => {
    const map = {};
    (detail?.context || []).forEach((c) => { (map[nodeKey(c.nodeType, c.nodeId)] ||= []).push(c); });
    return map;
  }, [detail]);
  // Deal / BU of each risk, from the aggregated context — only for RBAC on the action buttons.
  const riskScope = useMemo(() => {
    const map = {};
    (detail?.context || []).filter((c) => c.nodeType === "Risk").forEach((c) => {
      map[c.nodeId] ||= {};
      if (c.kind === "Deal") map[c.nodeId].dealId = c.refId;
      if (c.kind === "BusinessUnit") map[c.nodeId].businessUnitId = c.refId;
    });
    return map;
  }, [detail]);

  if (state === "loading" && !detail) return (
    <div className="py-8 flex items-center justify-center gap-2 text-sm text-content-muted" role="status">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading chain…
    </div>
  );
  if (state === "missing") return (
    <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-content-secondary">
      This chain is not available. It may have been resolved, or it is not visible with your current access.
      <div><button onClick={onBack} className="mt-2 text-indigo-600 hover:underline">Back to chains</button></div>
    </div>
  );
  if (state === "failed" || !detail) return <div className="py-8 text-center text-sm text-content-muted">The chain could not be loaded. <button className="text-indigo-600 hover:underline" onClick={load}>Try again</button></div>;

  const breakPointKey = detail.breakPoint ? nodeKey(detail.breakPoint.point.type, detail.breakPoint.point.id) : null;
  const riskMembers = members.filter((m) => m.type === "Risk");
  const anchorMember = members.find((m) => m.type === detail.anchorType && m.id === detail.anchorId);
  const topSeverity = riskMembers.map((m) => m.severity).filter(Boolean).sort((a, b) => SEVERITY_ORDER.indexOf(a) - SEVERITY_ORDER.indexOf(b))[0];
  const teams = new Set(riskMembers.map((m) => m.owner).filter(Boolean)).size;
  const affected = detail.reach ? detail.reach.affectedTotal : detail.connectedObjects.reduce((n, g) => n + g.items.length, 0) + riskMembers.length;
  const domains = detail.reach ? detail.reach.domainCount : new Set(detail.steps.map((s) => s.lane)).size;
  const hasReach = detail.reach?.groups?.length > 0;
  const tgl = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const updating = state === "loading";
  // Centre the chain on another point, remembering this chain so the user can come back to it.
  const openFrom = (type, id) => onOpen(type, id, { from: { type: detail.anchorType, id: detail.anchorId, label: detail.anchorLabel } });

  return (
    <div className="relative rounded-xl border border-border bg-theme-primary p-4" aria-busy={updating}>
      {updating && (
        <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs text-indigo-700" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading chain…
        </div>
      )}
      <div className={updating ? "opacity-50 pointer-events-none transition-opacity" : "transition-opacity"}>
      {previous && (
        <button type="button" onClick={onBackTo} className="mb-1.5 text-xs text-indigo-600 hover:underline">← Back to {previous.label}</button>
      )}
      <div className="flex items-center gap-2.5 flex-wrap mb-1">
        <span className="text-base font-semibold text-content-primary">{detail.anchorLabel}</span>
        {topSeverity && <span className={`text-xs px-2.5 py-0.5 rounded ${severityClass(topSeverity)}`} title="The most severe risk in this chain">Highest in chain: {topSeverity}</span>}
        {!detail.listed && <span className="text-[11px] px-1.5 py-0.5 rounded bg-theme-secondary text-content-secondary">re-anchored view</span>}
        <span className="ml-auto text-sm text-content-secondary">
          {anchorMember ? laneLabel(anchorMember.lane) : ""} · {members.length} points{detail.truncated ? " · more beyond this view" : ""}
        </span>
      </div>
      <p className="text-sm text-content-secondary mb-1.5">
        Chain flow — cumulative $ on arrows · risks in order from where the chain starts · click a risk or a shared object to centre the chain on it · click <b>why?</b> for the evidence
      </p>

      <FusionChainFlow detail={detail} breakPointKey={breakPointKey} contextByNode={contextByNode} onOpen={openFrom} onWhy={setWhyLinks}
        focusKeys={focusKeys} labelOf={labelOf} />

      <FusionConsequence consequence={detail.consequence} scenario={detail.scenario} listable={detail.listable} labelOf={labelOf} />

      <div className="flex gap-2 flex-wrap border-t border-border pt-2.5 mt-1 mb-2.5">
        <Toggle on={open.total} onClick={() => tgl("total")}>Correlated total</Toggle>
        <Toggle on={open.track} onClick={() => tgl("track")}>Track chain</Toggle>
        <Toggle on={open.history} onClick={() => tgl("history")}>How it formed</Toggle>
        <Toggle on={open.whatif} onClick={() => tgl("whatif")}>What-if</Toggle>
        {detail.breakPoint && <Toggle on={open.resolve} onClick={() => tgl("resolve")} title={BREAK_POINT_HINT}>◆ {BREAK_POINT}</Toggle>}
        {hasReach && <Toggle on={open.objects} onClick={() => tgl("objects")}>Affected objects</Toggle>}
      </div>

      {open.total && (
        <>
          <div className="flex items-baseline gap-2.5 flex-wrap mb-2.5">
            <span className="text-sm text-content-secondary flex items-center gap-1">Correlated exposure <HelpDot text={`${NON_ADDITIVE_TEXT} Shared risks are counted once within this figure.`} /></span>
            <span className="text-2xl font-semibold text-content-primary">{formatMoney(detail.exposure.mean)}</span>
            <span className="text-xs text-content-muted">
              {!detail.exposure.percentilesIncomplete && detail.exposure.p50 != null ? `P50 ${formatMoney(detail.exposure.p50)} · P90 ${formatMoney(detail.exposure.p90)} · ` : ""}
              {teams} team{teams === 1 ? "" : "s"} · affects <b>{affected}</b> objects across {domains} domains
            </span>
          </div>
          {detail.exposure.percentilesIncomplete && <div className="text-xs text-amber-700 -mt-1.5 mb-2">Calculation incomplete for P50/P90; the mean is exact.</div>}
        </>
      )}
      {open.track && (
        <FusionTrackPanel exposure={detail.exposure} reduction={detail.breakPoint?.meanRemovedAcrossChains ?? 0} anchorType={detail.anchorType} anchorId={detail.anchorId} partialView={detail.partialView} listable={detail.listable} />
      )}
      {open.history && <HistoryPanel history={detail.history} />}
      {open.whatif && <WhatIfPanel detail={detail} members={members} defaultKey={breakPointKey} onOpen={openFrom} />}
      {open.resolve && (
        <FusionLeverageCard breakPoint={detail.breakPoint} anchorType={detail.anchorType} anchorId={detail.anchorId}
          riskMembers={riskMembers} riskScope={riskScope} onOpen={openFrom} onActionDone={onActionDone} />
      )}
      {open.objects && <FusionReach reach={detail.reach} onOpen={openFrom} />}

      {whyLinks !== null && (
        <FusionWhyPopover links={whyLinks} labelOf={labelOf} onClose={() => setWhyLinks(null)} />
      )}
      </div>
    </div>
  );
};

/** "How this chain formed": when each risk was raised and each link first seen, oldest first. */
const HistoryPanel = ({ history }) => (
  <div className="rounded-lg border border-border bg-theme-secondary p-3 mb-2.5">
    <div className="font-semibold text-sm mb-1.5">How this chain formed</div>
    {history.length === 0 && <div className="text-xs text-content-muted">No dated events.</div>}
    {history.map((e, i) => (
      <div key={i} className="flex items-center gap-2 py-0.5">
        <span className={`w-2 h-2 rounded-full ${e.kind === "RiskRaised" ? "bg-red-500" : "bg-indigo-400"}`} />
        <span className="text-xs text-content-muted min-w-[72px]">{formatDate(e.at)}</span>
        <span className="text-sm">{e.kind === "RiskRaised" ? "Risk raised" : "Linked"}: {e.text}</span>
      </div>
    ))}
  </div>
);

/** What-if — removes only the risks directly attached to the selected point (Addendum A3). */
const WhatIfPanel = ({ detail, members, defaultKey, onOpen }) => {
  const apiCall = useApi();
  const [pointKey, setPointKey] = useState(defaultKey || (members[0] && nodeKey(members[0].type, members[0].id)));
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!pointKey) return;
    const [type, ...rest] = pointKey.split(":");
    let live = true;
    setFailed(false);
    fetchWhatIf(apiCall, { anchorType: detail.anchorType, anchorId: detail.anchorId, nodeType: type, nodeId: rest.join(":") })
      .then((r) => { if (live) setResult(r); })
      .catch(() => { if (live) { setResult(null); setFailed(true); } });
    return () => { live = false; };
  }, [apiCall, detail.anchorType, detail.anchorId, pointKey]);

  const riskLabel = (id) => members.find((m) => m.type === "Risk" && m.id === id)?.label || id;
  const others = result ? result.affectedChains.filter((c) => !(c.anchorType === detail.anchorType && c.anchorId === detail.anchorId)) : [];

  return (
    <div className="rounded-lg border border-indigo-200 bg-theme-secondary p-3 mb-2.5">
      <div className="font-semibold text-sm mb-1.5">What-if — simulate fixing a point</div>
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className="text-content-muted">Fix</span>
        <select aria-label="What-if point" value={pointKey || ""} onChange={(e) => setPointKey(e.target.value)} className="text-xs px-2 py-1 rounded-lg border border-border bg-theme-secondary">
          {defaultKey && !members.some((m) => nodeKey(m.type, m.id) === defaultKey) && (
            <option value={defaultKey}>{detail.breakPoint?.point.label || defaultKey}</option>
          )}
          {members.map((m) => <option key={nodeKey(m.type, m.id)} value={nodeKey(m.type, m.id)}>{m.label}</option>)}
        </select>
      </div>
      {failed && <div className="text-xs text-content-muted">The what-if could not be calculated.</div>}
      {result && (
        <>
          <div className="text-sm text-content-secondary mb-1.5">
            Removes the risks directly attached to this point: {result.removedRiskIds.map(riskLabel).join(", ") || "none"}.
          </div>
          <div className="text-sm text-content-secondary mb-1.5">
            {EXPECTED_LABEL}: <span className="line-through text-content-muted">{formatMoney(result.thisChain.meanBefore)}</span> → <b>{formatMoney(result.thisChain.meanAfter)}</b>
            {result.scenario && (
              <>
                <br />
                {SCENARIO_LABEL}: <span className="line-through text-content-muted">{formatMoney(result.scenario.exploitedBefore)}</span> → <b>{formatMoney(result.scenario.exploitedAfter)}</b>
                {result.scenario.upliftAfter < result.scenario.upliftBefore && (
                  <span className="text-xs text-content-muted"> (the fix also removes {formatMoney(result.scenario.upliftBefore - result.scenario.upliftAfter)} of chain effect)</span>
                )}
              </>
            )}
          </div>
          {others.length > 0 && <div className="text-sm text-content-secondary mb-1">If it is resolved, these {others.length} other chain{others.length === 1 ? "" : "s"} also drop:</div>}
          {others.map((c) => (
            <button key={`${c.anchorType}:${c.anchorId}`} onClick={() => onOpen(c.anchorType, c.anchorId)}
              className="w-full flex items-center gap-2 text-left text-sm rounded-lg border border-border px-2.5 py-1.5 mb-1.5 hover:bg-theme-hover">
              <span className="flex-1">{c.anchorLabel}</span>
              <span className="line-through text-content-muted text-xs">{formatMoney(c.meanBefore)}</span>
              <span className="text-green-700 font-semibold">↓ {formatMoney(c.reduction)}</span>
            </button>
          ))}
          <div className="text-xs text-green-700 mt-1">
            One fix removes {formatMoney(result.unionReduction)} across {result.affectedChains.length} chain{result.affectedChains.length === 1 ? "" : "s"}.
          </div>
          {result.affectedChainsIncomplete && <div className="text-xs text-amber-700 mt-1">More chains contain this point than are calculated here.</div>}
        </>
      )}
    </div>
  );
};

export default FusionChainView;
